/**
 * 로컬 Git 버전 관리 — 결정 #30(브라우저 내 Git). 카드/스크립트의 버전을 브라우저
 * 안(IndexedDB)에서 관리한다. 원격·토큰 없음(로컬 전용) — 새 비밀 표면이 0이라 가장
 * 안전하다(결정 #15 정신). 되돌리기(스냅샷)보다 강력한 "진짜 버전 추적·롤백"을 준다.
 *
 * isomorphic-git + lightning-fs(IndexedDB FS)를 **동적 import로 lazy-load**한다
 * (별도 청크라 초기 번들 0 영향, 결정 #1). git을 전혀 안 쓰는 사용자는 절대 내려받지
 * 않는다. 로드 실패 시 호출부가 graceful하게 처리한다.
 *
 * 플랫폼 경계(결정 #10): UI/상태는 이 서비스의 평탄한 함수만 쓴다. isomorphic-git
 * 타입은 이 모듈 안에 가둔다.
 */

// 타입만 import(빌드 시 erase). 실제 모듈은 lazy import.
import type FsType from "@isomorphic-git/lightning-fs";

/** 저장소 루트 경로(lightning-fs 가상 경로). */
const DIR = "/repo";
/** lightning-fs 인스턴스 이름(IndexedDB 저장소 키). */
const FS_NAME = "roqeet-git";

/** 로컬 전용이라 고정 저자(실명/신원 불필요, 결정 #8 익명 우선). */
const AUTHOR = { name: "RoQeet Launcher", email: "local@roqeet.com" } as const;

/** 커밋 한 건(히스토리 표시용). */
export interface GitCommitInfo {
  oid: string;
  message: string;
  /** 커밋 시각(epoch ms). */
  timestamp: number;
}

interface GitModule {
  git: typeof import("isomorphic-git");
  fs: InstanceType<typeof FsType>;
}

let modPromise: Promise<GitModule | null> | null = null;

/** isomorphic-git + lightning-fs를 한 번만 lazy-load. 실패 시 null. */
async function loadGit(): Promise<GitModule | null> {
  if (!modPromise) {
    modPromise = (async () => {
      const [gitMod, fsMod] = await Promise.all([
        import("isomorphic-git"),
        import("@isomorphic-git/lightning-fs"),
      ]);
      // isomorphic-git은 default 또는 namespace로 함수들을 노출(환경차 흡수).
      const rawGit = gitMod as unknown as { default?: typeof import("isomorphic-git") };
      const git = (rawGit.default ?? gitMod) as typeof import("isomorphic-git");
      const LightningFS = (fsMod as { default: typeof FsType }).default;
      const fs = new LightningFS(FS_NAME);
      return { git, fs };
    })().catch(() => null);
  }
  return modPromise;
}

/** git 사용 가능 여부(lazy-load 성공). false면 UI가 버전 기능을 숨긴다. */
export async function isGitAvailable(): Promise<boolean> {
  return (await loadGit()) !== null;
}

/** 저장소가 없으면 초기화한다(멱등). 이미 있으면 아무 일도 안 한다. */
async function ensureRepo(m: GitModule): Promise<void> {
  const { git, fs } = m;
  try {
    // .git/HEAD가 있으면 이미 초기화됨.
    await fs.promises.stat(`${DIR}/.git/HEAD`);
    return;
  } catch {
    // 없음 → 디렉토리 생성 후 init.
  }
  try {
    await fs.promises.mkdir(DIR);
  } catch {
    /* 이미 있을 수 있음 */
  }
  await git.init({ fs, dir: DIR, defaultBranch: "main" });
}

/** 중첩 경로의 부모 디렉토리를 보장한다(cards/ scripts/ 등). */
async function ensureParentDir(m: GitModule, filepath: string): Promise<void> {
  const idx = filepath.lastIndexOf("/");
  if (idx <= 0) return;
  const dir = `${DIR}/${filepath.slice(0, idx)}`;
  try {
    await m.fs.promises.mkdir(dir);
  } catch {
    /* 이미 있으면 무시 */
  }
}

/**
 * 파일 내용을 쓰고 커밋한다(결정 #30). 내용이 직전 커밋과 같으면 커밋하지 않고
 * undefined를 반환한다(빈 커밋 방지). 성공 시 새 커밋 oid 반환.
 */
export async function commitFile(
  filepath: string,
  content: string,
  message: string,
): Promise<string | undefined> {
  const m = await loadGit();
  if (!m) throw new Error("Git 모듈을 불러오지 못했습니다.");
  await ensureRepo(m);
  await ensureParentDir(m, filepath);

  const { git, fs } = m;
  const full = `${DIR}/${filepath}`;

  // 직전 내용과 동일하면 스킵(불필요한 커밋 방지).
  try {
    const prev = await fs.promises.readFile(full, { encoding: "utf8" });
    if (prev === content) return undefined;
  } catch {
    /* 새 파일 */
  }

  await fs.promises.writeFile(full, content, { encoding: "utf8" } as never);
  await git.add({ fs, dir: DIR, filepath });
  const oid = await git.commit({ fs, dir: DIR, message, author: { ...AUTHOR } });
  return oid;
}

/** 한 파일의 커밋 히스토리를 최신순으로 반환한다(없으면 빈 배열). */
export async function listHistory(filepath: string, limit = 50): Promise<GitCommitInfo[]> {
  const m = await loadGit();
  if (!m) return [];
  await ensureRepo(m);
  const { git, fs } = m;
  try {
    const commits = await git.log({ fs, dir: DIR, filepath, depth: limit });
    return commits.map((c) => ({
      oid: c.oid,
      message: c.commit.message.trim(),
      timestamp: (c.commit.author.timestamp ?? 0) * 1000,
    }));
  } catch {
    // filepath에 히스토리가 없거나 저장소가 비어 있음.
    return [];
  }
}

/** 특정 커밋 시점의 파일 내용을 읽는다(복원·미리보기용). 없으면 undefined. */
export async function readAtCommit(oid: string, filepath: string): Promise<string | undefined> {
  const m = await loadGit();
  if (!m) return undefined;
  await ensureRepo(m);
  const { git, fs } = m;
  try {
    const { blob } = await git.readBlob({ fs, dir: DIR, oid, filepath });
    return new TextDecoder().decode(blob);
  } catch {
    return undefined;
  }
}

// --- 엔티티별 안정 경로(버전 추적 단위) ---

/** 게임 카드 정의 파일 경로(카드 id 기준). */
export function gameCardPath(cardId: string): string {
  return `cards/${cardId}.json`;
}

/** 스크립트 소스 파일 경로(스크립트 id 기준). 확장자는 언어로. */
export function scriptPath(scriptId: string, language: string): string {
  const ext = language === "lua" ? "lua" : "js";
  return `scripts/${scriptId}.${ext}`;
}
